import { EmptyState } from '@components/EmptyState';
import { Checkbox } from '@components/ui/checkbox';
import { CopyButton } from '@components/ui/copy-button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@components/ui/table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import type { GenericTableProps, SortDirection, SortState } from '@/types/table';

// Sticky column positions — hardcoded for the cases table.
// Sticky-left is opt-in: only activates when a table includes the `sla_status` column
// (currently PQC only). Non-PQC and non-cases tables fall back to the prior layout.
const STICKY_RIGHT_KEYS = new Set(['status', 'row_actions']);
const STICKY_LEFT_KEYS_WITH_SLA = new Set(['selection', 'caseId', 'sla_status']);

interface StickyStyles {
  className: string;
  style: React.CSSProperties;
}

function getStickyStyles(
  key: string,
  variant: 'header' | 'cell' | 'loading',
  stickyRightOffset: Record<string, string>,
  stickyLeftOffset: Record<string, string>,
  stickyLeftEnabled: boolean,
): StickyStyles {
  const isLeft = stickyLeftEnabled && STICKY_LEFT_KEYS_WITH_SLA.has(key);
  const isRight = STICKY_RIGHT_KEYS.has(key);
  if (!isLeft && !isRight) return { className: '', style: {} };

  const position = isLeft ? stickyLeftOffset[key] || 'left-0' : stickyRightOffset[key] || 'right-0';
  // Inline `backgroundColor` (not just `bg-background` class) ensures the sticky cell
  // stays opaque during horizontal scroll — class-level bg lost specificity battles
  // with TableCell base styles and let scrolled content bleed through.
  // Use the theme token so the cell stays opaque in dark mode too.
  // For the selection cell, also pin width to 40px so its right edge exactly matches
  // the caseId sticky offset (left-10 = 40px); without this, a subpixel-width selection
  // cell leaves a sliver gap that scrolled content shows through.
  // Body cells need an explicit z-index so non-sticky scrolling cells don't paint over them.
  const base = `sticky ${position}`;
  const style: React.CSSProperties = { backgroundColor: 'hsl(var(--background))' };
  if (key === 'selection') {
    style.width = 40;
    style.minWidth = 40;
    style.maxWidth = 40;
  }

  if (variant === 'header') return { className: `${base} z-[25]`, style };
  if (variant === 'cell') {
    // `bg-muted!` (Tailwind !important) needed because inline backgroundColor would
    // otherwise win over class-based hover state.
    return {
      className: `${base} z-10 group-hover:bg-muted! transition-colors duration-200`,
      style,
    };
  }
  return { className: `${base} z-10`, style };
}

const MIN_SKELETON_WIDTH = 48;
const SKELETON_PADDING = 48;
const DEFAULT_SKELETON_WIDTH = 128;

const getSkeletonWidth = (columnWidth?: number) =>
  columnWidth
    ? Math.max(MIN_SKELETON_WIDTH, columnWidth - SKELETON_PADDING)
    : DEFAULT_SKELETON_WIDTH;

// Reusable sort indicator component
interface SortIndicatorProps {
  direction: SortDirection;
  priority?: number;
  isSortable: boolean;
}

function SortIndicator({ direction, priority, isSortable }: SortIndicatorProps) {
  if (!isSortable) return null;

  return (
    <div className="flex items-center">
      {direction === false ? (
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
      ) : direction === 'asc' ? (
        <ArrowUp className="h-4 w-4" />
      ) : (
        <ArrowDown className="h-4 w-4" />
      )}
      {priority !== undefined && (
        <span className="ml-1 text-xs text-muted-foreground">{priority}</span>
      )}
    </div>
  );
}

export function GenericTable<T>({
  config,
  data,
  loading = false,
  error = null,
  currentPage,
  hasNextPage,
  onPageChange,
  highlightId,
  searchTerm,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = [...PAGE_SIZE_OPTIONS],
  onPageSizeChange,
  selectionMode = false,
  selectedIds = new Set(),
  onSelectionChange,
  onSelectAllCurrentPage,
  sortState = [],
  onSortChange,
  hidePagination = false,
}: GenericTableProps<T>) {
  // Compute sticky right offsets based on whether row_actions column is present.
  // When row_actions is absent, status should be flush right (right-0) instead of offset.
  const hasRowActions = config.columns.some(col => col.key === 'row_actions');
  const stickyRightOffset = useMemo<Record<string, string>>(
    () => ({
      // row_actions is ~52px (32px button + 20px cell padding); status offsets by that amount
      status: hasRowActions ? 'right-[52px]' : 'right-0',
      row_actions: 'right-0',
    }),
    [hasRowActions],
  );

  // Sticky-left activates only for tables that include the redesigned `sla_status` column.
  const stickyLeftEnabled = config.columns.some(col => col.key === 'sla_status');
  // Sticky left offsets. When selectionMode is on, the checkbox cell (~40px / `w-10`)
  // also sticks left so it doesn't visually detach from Case ID during horizontal scroll.
  // caseId then offsets by 40px; sla_status by 40+140=180px.
  const stickyLeftOffset = useMemo<Record<string, string>>(
    () => ({
      selection: 'left-0',
      caseId: selectionMode ? 'left-10' : 'left-0',
      // Case ID cell is ~140px (font-mono case code + icons + padding).
      sla_status: selectionMode ? 'left-[180px]' : 'left-[140px]',
    }),
    [selectionMode],
  );

  // This ensures the same number of hooks are called on every render
  const [highlightedId, setHighlightedId] = useState<string | null>(highlightId || null);

  // Handle highlighting timeout
  useEffect(() => {
    if (highlightId) {
      setHighlightedId(highlightId);
      setTimeout(() => {
        setHighlightedId(null);
      }, 3000);
    }
    return undefined;
  }, [highlightId]);

  // Selection helper functions - always defined
  const getRowId = useCallback((item: T): string => config.getRowKey(item), [config]);

  const handleRowSelection = useCallback(
    (rowId: string, selected: boolean) => {
      if (!onSelectionChange) return;

      const newSelection = new Set(selectedIds);
      if (selected) {
        newSelection.add(rowId);
      } else {
        newSelection.delete(rowId);
      }
      onSelectionChange(newSelection);
    },
    [selectedIds, onSelectionChange],
  );

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (onSelectAllCurrentPage) {
        onSelectAllCurrentPage(selected);
      }
    },
    [onSelectAllCurrentPage],
  );

  // Calculate if all current page items are selected - memoized for performance
  const currentPageIds = useMemo(() => data.map(getRowId), [data, getRowId]);
  const allCurrentPageSelected = useMemo(
    () => currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.has(id)),
    [currentPageIds, selectedIds],
  );

  // Handle column sort click - memoized to prevent unnecessary re-renders
  const handleSort = useCallback(
    (field: string) => {
      if (!onSortChange) return;

      // Find if this field is already in sort state
      const existingIndex = sortState.findIndex(s => s.field === field);
      const newSortState: SortState[] = [...sortState];

      if (existingIndex >= 0) {
        // Field is already sorted - toggle direction or remove
        const currentDirection = sortState[existingIndex].direction;
        if (currentDirection === 'asc') {
          // Change to desc
          newSortState[existingIndex] = { field, direction: 'desc' };
        } else {
          // Remove from sort
          newSortState.splice(existingIndex, 1);
        }
      } else {
        // Add new sort field (ascending by default)
        newSortState.push({ field, direction: 'asc' });
      }

      onSortChange(newSortState);
    },
    [sortState, onSortChange],
  );

  // Get sort state for a column - memoized for performance
  const getColumnSort = useCallback(
    (field: string) => {
      const sortIndex = sortState.findIndex(s => s.field === field);
      if (sortIndex < 0) return { direction: false as const, priority: undefined };
      return {
        direction: sortState[sortIndex].direction,
        priority: sortState.length > 1 ? sortIndex + 1 : undefined,
      };
    },
    [sortState],
  );

  // Pagination state - computed before conditional returns
  const canGoPrevious = currentPage > 1;
  const canGoNext = hasNextPage;

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 1 && (newPage > currentPage ? canGoNext : canGoPrevious)) {
        onPageChange(newPage);
      }
    },
    [currentPage, canGoNext, canGoPrevious, onPageChange],
  );

  // NOW we can do conditional returns - all hooks have been called
  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col flex-1 py-2 min-h-0">
        <div className="flex-1 overflow-auto rounded-lg border border-bd-secondary relative min-h-0">
          <Table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
            <TableHeader className="shadow-[0_1px_0_0_rgb(229,231,235)] whitespace-nowrap">
              <TableRow>
                {selectionMode &&
                  (() => {
                    const sticky = getStickyStyles(
                      'selection',
                      'header',
                      stickyRightOffset,
                      stickyLeftOffset,
                      stickyLeftEnabled,
                    );
                    return (
                      <TableHead
                        className={`w-10 sticky top-0 z-20 bg-background ${sticky.className}`}
                        style={sticky.style}
                      >
                        <div className="h-4 bg-muted rounded w-4"></div>
                      </TableHead>
                    );
                  })()}
                {config.columns.map(column => {
                  const sortField = column.sortField || column.key;
                  const { direction, priority } = getColumnSort(sortField);
                  const isSortable = column.sortable && onSortChange;
                  const sticky = getStickyStyles(
                    column.key,
                    'header',
                    stickyRightOffset,
                    stickyLeftOffset,
                    stickyLeftEnabled,
                  );

                  return (
                    <TableHead
                      key={column.key}
                      className={`${column.headerClassName || ''} sticky top-0 z-20 bg-background ${sticky.className}`}
                      style={{
                        ...sticky.style,
                        ...(column.width
                          ? { width: column.width, minWidth: column.width, maxWidth: column.width }
                          : {}),
                      }}
                    >
                      {column.group && (
                        <div className="text-xs font-normal mb-0">{column.group}</div>
                      )}
                      <div className="flex items-center gap-2">
                        <span>{column.header}</span>
                        <SortIndicator
                          direction={direction}
                          priority={priority}
                          isSortable={!!isSortable}
                        />
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index} className="animate-pulse">
                  {selectionMode &&
                    (() => {
                      const sticky = getStickyStyles(
                        'selection',
                        'loading',
                        stickyRightOffset,
                        stickyLeftOffset,
                        stickyLeftEnabled,
                      );
                      return (
                        <TableCell
                          style={{ border: 'none', ...sticky.style }}
                          className={sticky.className}
                        >
                          <div className="h-4 bg-muted rounded w-4"></div>
                        </TableCell>
                      );
                    })()}
                  {config.columns.map(column => {
                    const sticky = getStickyStyles(
                      column.key,
                      'loading',
                      stickyRightOffset,
                      stickyLeftOffset,
                      stickyLeftEnabled,
                    );
                    return (
                      <TableCell
                        key={column.key}
                        className={sticky.className}
                        style={{
                          border: 'none',
                          ...sticky.style,
                          ...(column.width
                            ? {
                                width: column.width,
                                minWidth: column.width,
                                maxWidth: column.width,
                              }
                            : {}),
                        }}
                      >
                        <div
                          className="h-4 max-w-full rounded bg-muted"
                          style={{ width: getSkeletonWidth(column.width) }}
                        ></div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/50 rounded-lg p-4">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    const hasActiveFilters = searchTerm || (filters && Object.keys(filters).length > 0);
    const emptyState = config.emptyState || {
      title: 'No data found',
      description: hasActiveFilters
        ? 'Try adjusting your filters or search criteria.'
        : 'No data available yet.',
    };

    return (
      <div className="flex-1 flex flex-col">
        <EmptyState
          title={emptyState.title}
          description={emptyState.description}
          icon={FileText}
          iconClassName="text-muted-foreground"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 py-2 min-h-0">
      <div className="flex-1 overflow-auto rounded-lg border border-bd-secondary relative min-h-0">
        <Table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
          <TableHeader className="shadow-[0_1px_0_0_rgb(229,231,235)]">
            <TableRow>
              {selectionMode &&
                (() => {
                  const sticky = getStickyStyles(
                    'selection',
                    'header',
                    stickyRightOffset,
                    stickyLeftOffset,
                    stickyLeftEnabled,
                  );
                  return (
                    <TableHead
                      className={`w-10 sticky top-0 z-20 bg-background ${sticky.className}`}
                      style={sticky.style}
                    >
                      <div className="relative inline-flex items-center justify-center">
                        <Checkbox
                          checked={allCurrentPageSelected}
                          onCheckedChange={checked => handleSelectAll(!!checked)}
                          aria-label="Select all"
                        />
                        <div
                          className="absolute inset-0 w-8 h-8 -m-2 cursor-pointer"
                          onClick={() => handleSelectAll(!allCurrentPageSelected)}
                        />
                      </div>
                    </TableHead>
                  );
                })()}
              {config.columns.map(column => {
                const sortField = column.sortField || column.key;
                const { direction, priority } = getColumnSort(sortField);
                const isSortable = column.sortable && onSortChange;
                const sticky = getStickyStyles(
                  column.key,
                  'header',
                  stickyRightOffset,
                  stickyLeftOffset,
                  stickyLeftEnabled,
                );

                return (
                  <TableHead
                    key={column.key}
                    className={`${column.headerClassName || ''} whitespace-nowrap sticky top-0 z-20 bg-background ${isSortable ? 'cursor-pointer select-none' : ''} ${sticky.className}`}
                    style={{
                      ...sticky.style,
                      ...(column.width
                        ? { width: column.width, minWidth: column.width, maxWidth: column.width }
                        : {}),
                    }}
                    onClick={() => isSortable && handleSort(sortField)}
                  >
                    {column.group && <div className="text-xs font-normal">{column.group}</div>}
                    <div className="flex items-center gap-2">
                      <span>{column.header}</span>
                      <SortIndicator
                        direction={direction}
                        priority={priority}
                        isSortable={!!isSortable}
                      />
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody className="text-sm">
            {data.map(item => {
              const rowKey = config.getRowKey(item);
              const isHighlighted = highlightedId === rowKey;
              const isSelected = selectedIds.has(rowKey);
              const rowLink = config.getRowLink?.(item);
              const rowLinkState = config.getRowLinkState?.(item);
              const rowClassName = config.getRowClassName
                ? config.getRowClassName(item, isHighlighted)
                : `group hover:bg-muted cursor-pointer transition-colors duration-200 ${
                    isHighlighted ? 'case-highlight' : ''
                  } ${isSelected && selectionMode ? 'bg-muted/50' : ''}`;

              // Cell content renderer
              const renderCells = () => (
                <>
                  {selectionMode &&
                    (() => {
                      const sticky = getStickyStyles(
                        'selection',
                        'cell',
                        stickyRightOffset,
                        stickyLeftOffset,
                        stickyLeftEnabled,
                      );
                      return (
                        <TableCell style={sticky.style} className={`w-10 ${sticky.className}`}>
                          <div className="relative inline-flex items-center justify-center">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={checked => handleRowSelection(rowKey, !!checked)}
                              aria-label={`Select row ${rowKey}`}
                            />
                            <div
                              className="absolute inset-0 w-8 h-8 -m-2 cursor-pointer"
                              onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleRowSelection(rowKey, !isSelected);
                              }}
                            />
                          </div>
                        </TableCell>
                      );
                    })()}
                  {config.columns.map(column => {
                    const cellContent = column.accessor(item);
                    const copyValue =
                      column.copyable && column.copyValue ? column.copyValue(item) : '';
                    const shouldShowCopy = column.copyable && copyValue !== '';
                    const sticky = getStickyStyles(
                      column.key,
                      'cell',
                      stickyRightOffset,
                      stickyLeftOffset,
                      stickyLeftEnabled,
                    );

                    return (
                      <TableCell
                        key={column.key}
                        className={`${column.className || ''} whitespace-nowrap ${sticky.className}`}
                        style={{
                          ...sticky.style,
                          ...(column.width
                            ? {
                                width: column.width,
                                minWidth: column.width,
                                maxWidth: column.width,
                              }
                            : {}),
                        }}
                        data-testid={`cell-${column.key}`}
                      >
                        {shouldShowCopy ? (
                          <div className="flex min-w-0 max-w-full items-center gap-1 group">
                            {cellContent}
                            <CopyButton
                              value={copyValue}
                              label={column.header}
                              className="shrink-0 opacity-0 group-hover:opacity-100"
                            />
                          </div>
                        ) : (
                          cellContent
                        )}
                      </TableCell>
                    );
                  })}
                </>
              );

              return (
                <TableRow
                  key={rowKey}
                  className={rowClassName}
                  data-testid="table-row"
                  data-row-id={rowKey}
                >
                  {rowLink ? (
                    <Link
                      to={rowLink}
                      state={rowLinkState}
                      className="contents"
                      target={config.rowLinkNewTab ? '_blank' : undefined}
                      rel={config.rowLinkNewTab ? 'noopener noreferrer' : undefined}
                    >
                      {renderCells()}
                    </Link>
                  ) : (
                    renderCells()
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {!hidePagination && (data.length > 0 || currentPage > 1) && (
        <div className="flex items-center justify-between py-3">
          {/* Page Size Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={pageSize.toString()}
              onValueChange={value => onPageSizeChange?.(parseInt(value, 10))}
            >
              <SelectTrigger className="w-[70px] h-8" data-testid="page-size-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(option => (
                  <SelectItem
                    key={option}
                    value={option.toString()}
                    data-testid={`page-size-option-${option}`}
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Page Navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!canGoPrevious}
              className="flex items-center gap-1 p-1 text-sm font-medium text-foreground bg-background border border-input rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background"
              data-testid="prev-page-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="mx-2">
              <div
                className="w-[30px] h-[30px] flex items-center justify-center bg-background border border-input rounded-lg text-sm font-medium text-foreground"
                data-testid="current-page"
              >
                {currentPage}
              </div>
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!canGoNext}
              className="flex items-center gap-1 p-1 text-sm font-medium text-foreground bg-background border border-input rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background"
              data-testid="next-page-button"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

GenericTable.displayName = 'GenericTable';
